import React, { useMemo, useState } from 'react';
import { useBilling } from '../../contexts/BillingContext';
import { SalesDocument } from '../../types/billing';
import { formatMoney, fromN, toN } from '../../utils/billingMoney';
import { Dialog } from '../../components/ui/Dialog';
import { Button } from '../../components/ui/Button';

/** Applies existing account credit (deposits, overpayments, unapplied credit notes) to an invoice. */
export function ApplyCreditModal({
  invoice,
  onClose,
  onDone
}: {
  invoice: SalesDocument;
  onClose: () => void;
  onDone: (message?: string) => void;
}) {
  const {
    payments,
    creditNotes,
    applyCredit,
    config
  } = useBilling();
  const fmt = (v: number) => formatMoney(v, config.regional.currencySymbol);
  const sources = useMemo(() => [...payments.filter(p => p.accountId === invoice.accountId && p.state === 'Posted' && toN(p.unallocated) > 0).map(p => ({
    type: 'payment' as const,
    id: p.id,
    label: `${p.receiptNumber} · ${p.kind}`,
    available: p.unallocated
  })), ...creditNotes.filter(c => c.accountId === invoice.accountId && c.state === 'Issued' && toN(c.unallocated) > 0).map(c => ({
    type: 'creditNote' as const,
    id: c.id,
    label: `${c.number} · Credit note`,
    available: c.unallocated
  }))], [payments, creditNotes, invoice.accountId]);
  const [sourceKey, setSourceKey] = useState(sources[0] ? `${sources[0].type}:${sources[0].id}` : '');
  const source = sources.find(s => `${s.type}:${s.id}` === sourceKey);
  const max = source ? fromN(Math.min(toN(source.available), toN(invoice.balance))) : 0;
  const [amount, setAmount] = useState(String(max));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    if (!source) return;
    setBusy(true);
    setError('');
    const result = await applyCredit({
      sourceType: source.type,
      sourceId: source.id,
      invoiceId: invoice.id,
      amount: Number(amount)
    });
    setBusy(false);
    if (!result.ok) return setError(result.error ?? 'Could not apply credit.');
    onDone(result.message);
  };
  return <Dialog title={`Apply credit to ${invoice.number}`} subtitle={`Balance due ${fmt(invoice.balance)}`} onClose={onClose} footer={<>{error && <p role="alert" className="mr-auto text-sm text-red-700" data-id="element-2559" data-mp="2arpt5ydkqs72">{error}</p>}<Button variant="outline" onClick={onClose} data-id="element-2560" data-mp="2au2gkr23puri">Cancel</Button><Button onClick={submit} isLoading={busy} disabled={!source} data-id="element-2561" data-mp="26pcnf9ytuuaa">Apply credit</Button></>} data-id="element-2558" data-mp="2kpdxng0zfe2f">
      {sources.length === 0 ? <p className="text-sm text-slate-600" data-id="element-2562" data-mp="21svw66wbzzsz">{invoice.accountName} has no unapplied credit.</p> : <div className="space-y-4" data-id="element-2563" data-mp="1wz057oehjd7b">
          <div data-id="element-2564" data-mp="1s1hrzltfqbti">
            <label htmlFor="ac-source" className="text-sm font-medium text-slate-700" data-id="element-2565" data-mp="1s0ykkz1ev5tq">Credit source</label>
            <select id="ac-source" value={sourceKey} onChange={e => {
          setSourceKey(e.target.value);
          const s = sources.find(x => `${x.type}:${x.id}` === e.target.value);
          if (s) setAmount(String(fromN(Math.min(toN(s.available), toN(invoice.balance)))));
        }} className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-id="element-2566" data-mp="1n2wzy9oc6yg5">
              {sources.map(s => <option key={`${s.type}:${s.id}`} value={`${s.type}:${s.id}`} data-id="element-2567" data-mp="3pret16cwtdxo">{s.label} — {fmt(s.available)} available</option>)}
            </select>
          </div>
          <div data-id="element-2568" data-mp="2lnvcvj2gd0x">
            <label htmlFor="ac-amount" className="text-sm font-medium text-slate-700" data-id="element-2569" data-mp="2l5pd76lqkft">Amount to apply</label>
            <input id="ac-amount" type="number" min="0.01" step="0.01" max={max} value={amount} onChange={e => setAmount(e.target.value)} className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums" data-id="element-2570" data-mp="7hcxn6mh080x" />
            <p className="mt-1 text-xs text-slate-500" data-id="element-2571" data-mp="3fyqzqll51s3p">Up to {fmt(max)}.</p>
          </div>
        </div>}
    </Dialog>;
}